import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Wine, CalendarCheck, Sparkles } from 'lucide-react';

interface BarSectionProps {
  onOpenOrderApp: () => void;
}

export const BarSection: React.FC<BarSectionProps> = ({ onOpenOrderApp }) => {
  const { lang } = useLanguage();

  const text = {
    eyebrow: {
      fi: 'ARTESAANIBAARI / COCKTAILS & CRAFT',
      en: 'CRAFT BAR / COCKTAILS & SPIRITS',
      sv: 'HANTVERKSBAR / COCKTAILS & ÖL'
    },
    title: {
      fi: 'Savustettuja cocktaileja, villimarjoja ja paikallisia oluita',
      en: 'Smoked Libations, Wild Berries & Local Brews',
      sv: 'Rökta cocktails, vilda bär & lokala hantverksöl'
    },
    desc: {
      fi: 'Baarimme elää vuorokaudenajan mukaan: iltapäivällä nautitaan rennosta aperitiivista ja illan hämärtyessä nostamme maljat savustetuille viskidrinkeille ja viileille pienpanimo-oluille. Valikoimastamme löydät myös kotimaiset artesaaniginit ja aidon Helsingin Lonkeron.',
      en: 'Our cocktail bar moves with the rhythm of the city. From afternoon aperitivos to late-night smoked rye cocktails and crisp Finnish microbrews, our drink program is crafted to elevate your dining experience or stand proudly on its own.',
      sv: 'Vår bar erbjuder allt från eftermiddagsaperitifer till sena rökta signaturdrinkar och finsk hantverksöl. Vi serverar även klassisk finsk gin och äkta Lonkero.'
    },
    items: [
      {
        name: { fi: 'Smoked Rosemary Old Fashioned', en: 'Smoked Rosemary Old Fashioned', sv: 'Smoked Rosemary Old Fashioned' },
        desc: {
          fi: 'Puu-uunin hiilloksella savustettua ruisviskiä, appelsiinibitteriä ja käsinleikattu kirkas jää.',
          en: 'Wood-fired ember smoked Finnish rye whisky, aromatic orange bitters, and hand-carved ice.',
          sv: 'Rökt rågwhisky, apelsinbitter och handskuren klar is.'
        },
        tags: ['Signature Drink', '24% ABV'],
        price: '14.50 €'
      },
      {
        name: { fi: 'Nordic Lingonberry Spritz', en: 'Nordic Lingonberry Spritz', sv: 'Nordisk Lingonspritz' },
        desc: {
          fi: 'Villipuolukkauutetta, kuivaa italialaista proseccoa, tuoretta rosmariinia ja kuplavettä.',
          en: 'Lapland wild lingonberry reduction, dry Italian prosecco, fresh rosemary sprig, and soda.',
          sv: 'Vilda lingon, torr italiensk Prosecco, rosmarin och soda.'
        },
        tags: ['Raikas Aperitiivi', '11% ABV'],
        price: '12.90 €'
      },
      {
        name: { fi: 'Fat Lizard Ankle Breaker IPA (0.44L)', en: 'Fat Lizard Craft IPA 5.2% (0.44L)', sv: 'Fat Lizard Hantverks-IPA (0.44L)' },
        desc: {
          fi: 'Espoossa pantu hedelmäisen humaloitu ja mehukas suomalainen käsityöläisolut.',
          en: 'Locally brewed in Espoo Finland, a juicy and tropical American-style craft IPA.',
          sv: 'Lokalt bryggd fruktig och tropiskt humlad IPA från Esbo.'
        },
        tags: ['Kotimainen Pienpanimo', '5.2% ABV'],
        price: '9.20 €'
      },
      {
        name: { fi: 'Hartwall Original Long Drink "Lonkero"', en: 'Original Finnish "Lonkero" 5.5%', sv: 'Original Gin Long Drink "Lonkero"' },
        desc: {
          fi: 'Helsingin 1952 olympialaisiin kehitetty ikoninen aito gini-greippijuoma.',
          en: 'The legendary Finnish gin & grapefruit long drink, iconically created for the 1952 Helsinki Olympics.',
          sv: 'Klassisk finsk gin- och grapefruktdryck från OS i Helsingfors 1952.'
        },
        tags: ['Helsinki Heritage', '5.5% ABV'],
        price: '8.50 €'
      }
    ],
    btn: {
      fi: 'Varaa Pöytä & Tule Drinkeille',
      en: 'Reserve Table for Drinks',
      sv: 'Boka Bord för Drinkar'
    }
  };

  return (
    <section id="bar" className="os-page-section">
      <div className="os-container">
        <div className="os-feature-split reverse-mobile">
          {/* Story & Drink Cards */}
          <div>
            <div className="os-section-header" style={{ marginBottom: '28px' }}>
              <span className="os-eyebrow">{text.eyebrow[lang]}</span>
              <h2 className="os-section-title">{text.title[lang]}</h2>
              <p className="os-section-desc">{text.desc[lang]}</p>
            </div>

            <div className="os-menu-cards-list">
              {text.items.map((item, idx) => (
                <div key={idx} className="os-menu-card">
                  <div style={{ flex: 1 }}>
                    <h3 className="os-dish-name">{item.name[lang]}</h3>
                    <p className="os-dish-desc">{item.desc[lang]}</p>
                    <div className="os-dish-tags">
                      {item.tags.map((t, tIdx) => (
                        <span key={tIdx} className="os-pill-tag amber">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="os-dish-price">{item.price}</div>
                </div>
              ))}
            </div>

            {/* Clear Section Action */}
            <div style={{ marginTop: '28px' }}>
              <button onClick={onOpenOrderApp} className="os-btn-book-table" style={{ padding: '14px 28px', fontSize: '1rem' }}>
                <CalendarCheck size={18} />
                <span>{text.btn[lang]}</span>
              </button>
            </div>
          </div>

          {/* Media Column */}
          <div className="os-feature-media">
            <img
              src="/images/craft-cocktails-bar.jpg"
              alt="Artisanal craft cocktails and Finnish spirits at The Oven Story bar"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
